import { ErrorResponse } from '@/api/Base/types'

export const LICENSE_ERROR_CODES = {
  ALL_CHECKS_FAILED: 2300001,
  CLIENT_SERVER_MISMATCH: 2300002,
} as const

export type LicenseErrorCode = typeof LICENSE_ERROR_CODES[keyof typeof LICENSE_ERROR_CODES]

export type LicenseErrorSource = 'query' | 'mutation' | 'socket'

export interface LicenseErrorPayload {
  uniqueCode: LicenseErrorCode
  status: number | null
  message: string | null
  source: LicenseErrorSource
  occurredAt: number
}

interface ErrorResponseLike {
  response?: {
    status?: number
    data?: Partial<ErrorResponse>
  }
}

export const isLicenseErrorCode = (uniqueCode: unknown): uniqueCode is LicenseErrorCode => (
  uniqueCode === LICENSE_ERROR_CODES.ALL_CHECKS_FAILED
  || uniqueCode === LICENSE_ERROR_CODES.CLIENT_SERVER_MISMATCH
)

export const getErrorUniqueCode = (error: unknown): unknown => (
  (error as ErrorResponseLike | undefined)?.response?.data?.unique_code
)

export const isLicenseError = (error: unknown): boolean => isLicenseErrorCode(getErrorUniqueCode(error))

export const toLicenseErrorPayload = (
  error: unknown,
  source: LicenseErrorSource
): LicenseErrorPayload | null => {
  const errorLike = error as ErrorResponseLike | undefined
  const uniqueCode = errorLike?.response?.data?.unique_code

  if (!isLicenseErrorCode(uniqueCode)) {
    return null
  }

  return {
    uniqueCode,
    status: errorLike?.response?.status ?? null,
    message: errorLike?.response?.data?.message ?? null,
    source,
    occurredAt: Date.now(),
  }
}

export class LicenseBoundaryError extends Error {
  uniqueCode: LicenseErrorCode

  status: number | undefined

  constructor(payload: LicenseErrorPayload) {
    super(payload.message ?? 'License verification failed')
    this.name = 'LicenseBoundaryError'
    this.uniqueCode = payload.uniqueCode
    this.status = payload.status ?? undefined
  }
}

export const isLicenseBoundaryError = (error: unknown): error is LicenseBoundaryError => (
  error instanceof LicenseBoundaryError
)
